import React, { useEffect, useCallback, useMemo } from 'react'
import { Collapse } from 'react-bootstrap'
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io'
import styles from '../styles/profile-styles.module.scss'
import i18n from 'constants/strings'
import useMultiToggle from 'hooks/use-multi-toggle'
import WTNSpinner from 'components/generic/WTNSpinner'
import { ProfileSection, Provider, TournamentUI } from 'types/global'
import LoadMore from 'components/generic/LoadMore'
import { connect, useDispatch, useSelector } from 'react-redux'
import {
  getMatchResultListings,
  GetMatchResultListingsOptions,
  setTournamentsDateRange,
} from 'actions/profile-actions'
import { AppState } from 'redux/reducers'
import memo from 'memoize-one'
import useWindowSize from 'hooks/use-windowSize'
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from 'constants/global'
import MobileFilter from 'components/generic/MobileFilterSelector/MobileFilterSelector'
import { createDisplayDate, splitAt } from 'utils'
import CompetitionMatchCard from './CompetitionMatchCard'
import classNames from 'classnames'
import NoScoresPanel from '../social-matches/panels/NoScoresPanel'
import { Trans } from 'react-i18next'
import { DateRange } from 'components/generic/DatePicker/types'
import DateRangePickerWTN from 'components/generic/DatePicker/DateRangePickerWTN'
import { StringParam, useQueryParams } from 'use-query-params'

const DateRangeDisplay = ({
  start,
  end,
}: {
  start: string | undefined
  end: string | undefined
}) => {
  if (!start || !end) {
    return <>{i18n.t('strData.na')}</>
  }
  return (
    <>
      <time dateTime={start}>{createDisplayDate(new Date(start))}</time> -{' '}
      <time dateTime={end}>{createDisplayDate(new Date(end), { withYear: true })}</time>
    </>
  )
}

interface Props {
  playerId: string
  isFetching: boolean
  matchResults: TournamentUI[]
  firstTournamentDate: string
  tournamentsDateRange: { start: string; end: string }
  getMatchResultListings: (options: GetMatchResultListingsOptions) => void
  tennisId: string
}

const MatchResultListings: React.FC<Props> = ({
  playerId,
  isFetching,
  matchResults,
  getMatchResultListings,
  firstTournamentDate,
  tournamentsDateRange,
  tennisId,
}) => {
  const tournamentsPlayed = useSelector((state: AppState) => state.profile.totalTournamentsPlayed)
  const windowWidth = useWindowSize().width
  const dispatch = useDispatch()
  const [_, setQuery] = useQueryParams({
    section: StringParam,
    vsPlayerId: StringParam,
  })
  const handleH2hClick = useCallback(
    playerIds => {
      const opponentId = playerIds.find(id => id !== playerId)
      if (opponentId && typeof opponentId === 'string')
        setQuery({
          section: ProfileSection.H2H,
          vsPlayerId: opponentId,
        })
    },
    [playerId, setQuery]
  )
  const [isShown, toggleShow, , setShown, resetShown] = useMultiToggle()
  const validTournaments = useMemo(() => matchResults.filter(t => t.id), [matchResults])
  const allExpanded = useMemo(() => {
    return validTournaments.every(t => isShown(t.id))
  }, [validTournaments, isShown])
  const toggleShowAll = useCallback(() => {
    allExpanded || !validTournaments ? resetShown() : setShown(validTournaments.map(r => r.id))
  }, [setShown, resetShown, validTournaments, allExpanded])
  const handleApply = useCallback(
    (range: DateRange) => {
      dispatch(
        setTournamentsDateRange({
          start: range?.start?.toISOString(),
          end: range?.end?.toISOString(),
        })
      )
    },
    [dispatch]
  )

  const { minPickerDate, maxPickerDate } = useMemo(
    () => ({
      minPickerDate: firstTournamentDate ? new Date(firstTournamentDate) : undefined,
      maxPickerDate: new Date(),
    }),
    [firstTournamentDate]
  )

  const selectedDates: DateRange = useMemo(() => {
    const start = tournamentsDateRange.start || firstTournamentDate
    const end = tournamentsDateRange.end
    const dateStart = start ? new Date(start) : undefined
    const dateEnd = end ? new Date(end) : new Date()

    return { start: dateStart, end: dateEnd }
  }, [tournamentsDateRange, firstTournamentDate])

  const setTournamentsDateRangeMemo = useCallback(
    (dateRange: { start: string; end: string }) => {
      console.log(dateRange.start, dateRange.end)
      dispatch(setTournamentsDateRange(dateRange))
    },
    [dispatch]
  )

  const dateRangeConfigMobile = useMemo(() => {
    const { start, end } = selectedDates

    if (!start || !end || !minPickerDate || !maxPickerDate) return null
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      minDate: minPickerDate.toISOString(),
      maxDate: maxPickerDate.toISOString(),
      timeRangeDispatch: setTournamentsDateRangeMemo,
    }
  }, [selectedDates, setTournamentsDateRangeMemo, minPickerDate, maxPickerDate])

  useEffect(() => {
    const { start, end } = tournamentsDateRange
    getMatchResultListings({
      skip: 0,
      start,
      end,
      resetMatchResultListings: true,
    })
  }, [getMatchResultListings, tournamentsDateRange])

  return (
    <>
      {windowWidth >= TABLET_BREAKPOINT && (
        <>
          {firstTournamentDate && (
            <DateRangePickerWTN
              containerClassName={styles.datePickerWrapper}
              minDate={minPickerDate}
              maxDate={maxPickerDate}
              selectedRange={selectedDates}
              setSelectedRange={handleApply}
            />
          )}
        </>
      )}
      {windowWidth < TABLET_BREAKPOINT && firstTournamentDate && dateRangeConfigMobile && (
        <MobileFilter
          timeRangeFilterData={dateRangeConfigMobile}
          classNames={{ filterBar: styles.filterBar }}
        />
      )}
      {isFetching ? (
        <div className={styles.matchResultLoadingWrapper}>
          <WTNSpinner width={125} />
        </div>
      ) : validTournaments.length === 0 ? (
        <NoScoresPanel
          title={i18n.t('strMatchResults.noResults.title')}
          info={i18n.t('strMatchResults.noResults.info')}
        />
      ) : (
        <>
          <div className={styles.showAllWrapper}>
            <button className={styles.showAll} onClick={toggleShowAll} type='button'>
              <span>
                {allExpanded
                  ? i18n.t('strMatchResults.closeAllResults')
                  : i18n.t('strMatchResults.openAllResults')}
              </span>
              {allExpanded ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  transform='rotate(180)'
                  width='26'
                  height='26'
                  fill='none'
                  viewBox='0 0 26 26'
                >
                  <path
                    stroke='#DC3741'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M17.875 10.4L13 15.6l-4.875-5.2'
                  />
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='26'
                  height='26'
                  fill='none'
                  viewBox='0 0 26 26'
                >
                  <path
                    stroke='#DC3741'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M17.875 10.4L13 15.6l-4.875-5.2'
                  />
                </svg>
              )}
            </button>
          </div>

          <ul className={styles.tournamentsList}>
            {validTournaments.map((t, i) => {
              return (
                <li key={t.id} className={styles.matchResultContainer}>
                  <ResultListingDropdown
                    selected={isShown(t.id)}
                    onClick={() => toggleShow(t.id)}
                    title={t.name}
                    start={t.start}
                    end={t.end}
                    key={t.id}
                    provider={t.provider}
                  />
                  <Collapse in={isShown(t.id)} mountOnEnter unmountOnExit>
                    {t.results && (
                      <>
                        {t?.provider?.displayName && t?.provider?.logoUrl && (
                          <div className={styles.providerLinkSection}>
                            <img
                              src={t.provider.logoUrl}
                              className={styles.providerLinkLogo}
                              alt=''
                            />
                            <div>
                              <p>
                                <Trans
                                  ns='strProviderLink'
                                  values={{ providerName: t.provider.displayName }}
                                  i18nKey={'content'}
                                >
                                  <>Data supplied by the {t.provider.displayName}. </>
                                </Trans>
                                {t?.provider?.landingPageUrl && (
                                  <Trans
                                    ns='strProviderLink'
                                    values={{ providerName: t.provider.displayName }}
                                    i18nKey={'landing'}
                                  >
                                    <>
                                      View details on the{' '}
                                      <a
                                        href={`${t.provider.landingPageUrl}?tennisId=${tennisId}&tournamentId=${t?.providerTournamentId}`}
                                        className={styles.providerLink}
                                      >
                                        {t.provider.displayName} website
                                      </a>
                                      .
                                    </>
                                  </Trans>
                                )}
                              </p>

                              {t?.provider?.displayName && t?.provider?.supportPageUrl && (
                                <Trans
                                  ns='strProviderLink'
                                  values={{ providerName: t.provider.displayName }}
                                  i18nKey={'support'}
                                >
                                  <p>
                                    Something not right?{' '}
                                    <a
                                      href={`${t.provider.supportPageUrl}`}
                                      className={styles.providerSupportLink}
                                    >
                                      Contact the {t.provider.displayName}
                                    </a>
                                    .
                                  </p>
                                </Trans>
                              )}
                            </div>
                          </div>
                        )}

                        <ul className={styles.cardContainer}>
                          {t.results.map(result => (
                            <li key={result.id} className={styles.cardItem}>
                              <CompetitionMatchCard
                                key={result.id}
                                match={result}
                                profileViewedId={playerId}
                                truncateName={windowWidth < MOBILE_BREAKPOINT}
                                onClickH2H={handleH2hClick}
                              />
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Collapse>
                </li>
              )
            })}
          </ul>

          {tournamentsPlayed > matchResults.length && (
            <div className={styles.loadMoreBtnContainer}>
              <LoadMore
                loadMore={() => {
                  const { start, end } = tournamentsDateRange
                  getMatchResultListings({
                    skip: matchResults.length,
                    start,
                    end,
                  })
                  resetShown()
                }}
              />
            </div>
          )}
        </>
      )}
    </>
  )
}

export const ResultListingDropdown: React.FC<{
  selected: boolean
  onClick?: () => void
  title: string
  start?: string
  end?: string
  provider?: Provider
}> = ({ selected, onClick, title, start, end, provider }) => {
  const Chevron = selected ? IoIosArrowUp : IoIosArrowDown

  return (
    <article
      tabIndex={onClick && 0}
      role={onClick ? 'button' : 'presentation'}
      className={classNames(styles.matchResultDropdown, styles.chevronContainer, {
        [styles.matchResultDropdownActive]: selected,
        [styles.chevronContainerActive]: selected,
      })}
      onClick={onClick}
      onKeyDown={
        onClick &&
        (evt => {
          if (evt.key === 'Enter') onClick()
        })
      }
      aria-expanded={onClick && selected}
    >
      <h5 className={styles.matchResultsTournamentName} aria-label={title}>
        {title
          ? splitAt(title, '/').map((chunk, i) => (
              <span key={i} aria-hidden>
                {chunk}
              </span>
            ))
          : title}
      </h5>
      <div className={styles.matchResultsTournamentDateAndProvider}>
        {provider?.displayName && (
          <p
            className={classNames(styles.matchResultsTournamentProvider, {
              [styles.matchResultsTournamentProviderActive]: selected,
            })}
          >
            {provider?.displayName}
          </p>
        )}
        <p className={styles.matchResultsTournamentDate}>
          <DateRangeDisplay start={start} end={end} />
        </p>
      </div>
      <Chevron className={styles.chevron} aria-hidden />
    </article>
  )
}

const mapStateToProps = ({ profile }: AppState) => ({
  isFetching: profile.matchResultsLoading,
  matchResults: profile.matchResultListings,
  firstTournamentDate: profile.firstTournamentDate,
  tournamentsDateRange: profile.tournamentsDateRange,
})

const memoizedDispatch = memo((dispatch, pId) => ({
  getMatchResultListings: (options: GetMatchResultListingsOptions) =>
    pId && dispatch(getMatchResultListings(pId, options)),
}))

const mapDispatchToProps = (dispatch, ownProps) => memoizedDispatch(dispatch, ownProps.playerId)

export default connect(mapStateToProps, mapDispatchToProps)(MatchResultListings)
